<template>
    <button
        :id="id"
        class="dt-button"
        :class="[type, { loading: isLoading }]"
        @click="handleClick"
        :disabled="isLoading"
    >
        <span class="button-content">
            <template v-if="icon && iconPosition === 'left'">
                <img :src="icon" :style="{ width: iconSize }" />
            </template>
            {{ text }}
            <template v-if="icon && iconPosition === 'right'">
                <img :src="icon" :style="{ width: iconSize }" />
            </template>
        </span>
        <span class="button-spinner" v-if="isLoading">
            <LoadingIndicator :size="24" />
        </span>
    </button>
</template>

<script>
import EventBus from "../event-bus";
import LoadingIndicator from "./LoadingIndicator.vue";
import { getInAppRoute } from "../utils/inAppNavigation";

export default {
    name: "DTButton",
    components: {
        LoadingIndicator,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        type: {
            type: String,
            default: "primary",
            validator: function (value) {
                return ["primary", "secondary", "tertiary"].includes(value);
            },
            required: true,
        },
        text: {
            type: String,
            default: "Button",
            required: true,
        },
        link: {
            type: String,
            default: "",
        },
        target: {
            type: String,
            default: "_self",
            validator: function (value) {
                return ["_self", "_blank"].includes(value);
            },
        },
        icon: {
            type: String,
            default: "",
            validator: function (value) {
                return value === "" || /\.(jpeg|jpg|png|svg)$/i.test(value);
            },
        },
        iconPosition: {
            type: String,
            default: "left",
            validator: function (value) {
                return ["left", "right"].includes(value);
            },
        },
        iconSize: {
            type: String,
            default: "24px",
        },
        eventBusEmit: {
            type: String,
            default: "",
        },
        externalLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            shortLoading: false
        };
    },
    computed: {
        isLoading() {
            return this.externalLoading || this.shortLoading;
        }
    },
    methods: {
        handleClick() {
            const slowEvents = ["orderNow", "orderNowDodoPayments"];
            if (slowEvents.includes(this.eventBusEmit)) {
                this.shortLoading = true;
                
                setTimeout(() => {
                    this.shortLoading = false;
                }, 4000);
            }

            if (this.eventBusEmit) {
                EventBus.emit(this.eventBusEmit);
            }

            if (this.link) {
                this.openLink();
            }
        },
        openLink() {
            // A step link to the page the customer is on goes through the router, so the
            // step guard checks the required fields instead of the page reloading.
            const route = this.target === "_self" && this.$router ? getInAppRoute(this.link) : null;
            if (route) {
                this.$router.push(route);
                return;
            }

            window.open(this.link, this.target);
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-button {
    border-radius: 8px;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    column-gap: 8px;
    padding: 20px 40px;
    border: none;
    position: relative;

    .button-content {
        display: flex;
        align-items: center;
        gap: 8px;
        transition: opacity 0.2s ease;
    }

    .button-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
    }


    &.loading .button-content {
        opacity: 0;
    }

    &.primary {
        background-color: $color-primary-dt-blue;
        font-size: 18px;
        font-weight: 700;
        line-height: 20px;
        color: $color-new-black;
        transition: background-color 0.3s, box-shadow 0.3s;

        @media (max-width: $breakpoint-tablet) {
            background-color: $color-primary-dt-blue;
        }

        &:hover:not(:disabled) {
            background: $color-dt-dark-blue-1;
            box-shadow: 0px 3px 12px 0px rgba(238, 68, 238, 0.06);
            cursor: pointer;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    &.secondary {
        background-color: $color-white;
        font-size: 16px;
        font-weight: 700;
        line-height: 20px;
        color: $color-new-black;
        box-shadow: inset 0 0 0 2px $color-primary-dt-blue;

        &:hover:not(:disabled) {
            cursor: pointer;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
}
</style>
